/**
 * SaveBar — the sticky-bottom action bar inside every settings form pane.
 *
 * Mirrors `prototype/settings/settings-company.html ▸ .save-bar`:
 *   - Status chip on the left: idle dot, blinking amber when dirty, green
 *     check when just-saved.
 *   - Discard + Save buttons on the right.
 *
 * State is owned by the form (RHF or plain useState — caller's choice);
 * this component is pure presentation. The "last edited" timestamp is
 * formatted by the caller too — that way the same bar can show "5 min
 * ago" or absolute clock based on the host's preference.
 */
import { Check } from "lucide-react";
import { cn, Button } from "@flint/ui";

export type SaveBarState = "idle" | "dirty" | "saving" | "saved";

export interface SaveBarProps {
  state: SaveBarState;
  /** Status copy shown next to the dot. Hosts pass strings like
   *  "All changes saved · 2 min ago" or "Unsaved changes". */
  statusText: string;
  onDiscard: () => void;
  onSave: () => void;
}

export function SaveBar({ state, statusText, onDiscard, onSave }: SaveBarProps) {
  const dotClass =
    state === "dirty" || state === "saving"
      ? "bg-warn animate-pulse"
      : state === "saved"
        ? "bg-ok"
        : "bg-line-2";

  const showSaved = state === "saved";
  const disableActions = state === "idle" || state === "saved" || state === "saving";

  return (
    <div
      // The form pane is `flex-col gap-4`; this bar sits at the bottom and
      // stays sticky to the page-pane container. Border + shadow lift it
      // visually so the user always knows there's an action shelf available.
      className="
        sticky bottom-4 z-10
        mt-4
        flex items-center gap-3 flex-wrap
        bg-paper border border-line rounded-md shadow-card
        px-4 py-3
      "
    >
      <div className="flex items-center gap-2 flex-1 min-w-0 text-xs text-muted">
        {showSaved ? (
          <Check size={12} strokeWidth={3} className="text-ok shrink-0" aria-hidden />
        ) : (
          <span className={cn("w-1.5 h-1.5 rounded-full shrink-0", dotClass)} aria-hidden />
        )}
        <span className="truncate">{statusText}</span>
      </div>
      <Button
        type="button"
        intent="ghost"
        size="sm"
        className="h-11"
        onClick={onDiscard}
        disabled={disableActions}
      >
        Discard
      </Button>
      <Button
        type="button"
        intent="primary"
        size="sm"
        className="h-11"
        onClick={onSave}
        disabled={disableActions}
      >
        {state === "saving" ? "Saving…" : "Save changes"}
      </Button>
    </div>
  );
}
